import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Autocomplete,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import axios from "axios";
import NotificationSnackbar from "./NotificationBar";

const emptyForm = {
  name: "",
  tin: "",
  ifmisCode: "",
  countryId: "",
  currencyId: "",
};
function EditCostcenterModal({
  open,
  onClose,
  onSave,
  onSaveSuccess,
  costcenter,
}) {
  const theme = useTheme();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (!open) return;

    const fetchCountries = async () => {
      try {
        setLoadingCountries(true);
        const response = await axios.get("/api/support/countries");
        const data = response.data;
        setCountries(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching countries:", error);
        setSnackbarMessage("Failed to load countries");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoadingCountries(false);
      }
    };

    const fetchCurrencies = async () => {
      try {
        setLoadingCurrencies(true);
        const response = await axios.get("/api/support/currencies");
        const data = response.data;
        setCurrencies(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching currencies:", error);
        setSnackbarMessage("Failed to load currencies");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoadingCurrencies(false);
      }
    };
    fetchCountries();
    fetchCurrencies();
  }, [open]);

  useEffect(() => {
    if (costcenter && open) {
      setForm({
        name: costcenter.costcenter_name ?? "",
        tin: costcenter.tinNumber ?? "",
        ifmisCode: costcenter.ifmisCode ?? "",
        countryId:
          costcenter.countryId ??
          costcenter.country_id ??
          costcenter.CountryId ??
          "",
        currencyId: costcenter.currencyId ?? costcenter.currency_id ?? "",
      });
      setErrors({});
      setSaved(false);
    }
  }, [costcenter, open]);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };
  const handleCountryChange = (event, selectedCountry) => {
    const countryId = selectedCountry ? selectedCountry.id : "";
    setForm((prev) => ({
      ...prev,
      countryId,
    }));
    setErrors((prev) => ({
      ...prev,
      countryId: "",
    }));
  };

  const handleCurrencyChange = (event, selectedCurrency) => {
    const currencyId = selectedCurrency ? selectedCurrency.id : "";
    setForm((prev) => ({
      ...prev,
      currencyId,
    }));
    setErrors((prev) => ({
      ...prev,
      currencyId: "",
    }));
  };
  const selectedCountry =
    countries.find(
      (country) => String(country.id) === String(form.countryId),
    ) || null;
  const selectedCurrency =
    currencies.find(
      (currency) => String(currency.id) === String(form.currencyId),
    ) || null;

  const validate = () => {
    const errs = {};
    if (!form.name) errs.name = "Costcenter name is required";
    if (!form.tin) errs.tin = "TIN number is required";
    if (!form.ifmisCode) errs.ifmisCode = "IFMIS code is required";
    if (!form.countryId) errs.countryId = "Country is required";
    if (!form.currencyId) errs.currencyId = "Currency is required";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const response = await axios.put(
        `/api/support/update-costcenters/${costcenter.id}`,
        form,
      );
      setSnackbarMessage(
        response.data?.message || "Costcenter updated successfully",
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setSaved(true);
      onSave?.(form);
      setTimeout(() => {
        setSaved(false);
        onSaveSuccess?.();
        onClose();
      }, 1000);
    } catch (err) {
      console.error("Save error:", err);
      const errorMessage =
        err.response?.data?.message || err.message || "Something went wrong";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    setForm(emptyForm);
    setSaved(false);
    onClose();
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: alpha(theme.palette.primary.light, 0.08),
      "& fieldset": { borderColor: alpha(theme.palette.primary.light, 0.5) },
      "&:hover fieldset": { borderColor: theme.palette.primary.light },
      "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  };
  const readOnlyFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: alpha(theme.palette.action.disabled, 0.05),
      "& fieldset": { borderColor: alpha(theme.palette.primary.light, 0.3) },
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: theme.palette.text.primary,
      fontWeight: 500,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      sx={{
        backgroundColor: "rgba(0, 0, 0, 0.7)",
      }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "visible",
          border: `1px solid ${alpha(theme.palette.primary.light, 0.5)}`,
        },
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          bgcolor: theme.palette.primary.light,
          px: 2.5,
          pt: 2.5,
          pb: 2,
          borderRadius: "12px 12px 0 0",
        }}
      >
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditIcon sx={{ fontSize: 16, color: "#fff" }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 500,
                  color: "#fff",
                  lineHeight: 1.3,
                }}
              >
                Edit Costcenter
              </Typography>
              <Typography
                sx={{ fontSize: 12, color: "rgba(255,255,255,0.7)", mt: 0.3 }}
              >
                Update costcenter details
              </Typography>
            </Box>
          </Box>

          <IconButton
            size="small"
            onClick={handleClose}
            sx={{
              color: "rgba(255,255,255,0.8)",
              border: "1px solid rgba(255,255,255,0.3)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* CURRENT VALUE */}
        {costcenter?.id && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mt: 1.5,
              px: 1.5,
              py: 0.75,
              borderRadius: "20px",
              bgcolor: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          >
            <Box
              sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#fff" }}
            />
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.65)" }}>
              ID:
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: "#fff" }}>
              {costcenter.id}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>
              ·
            </Typography>
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
              {costcenter.costcenter_name}
            </Typography>
          </Box>
        )}
      </Box>

      {/* FORM */}
      <DialogContent
        sx={{
          pt: 2.5,
          pb: 1,
          bgcolor: "#fff",
          overflow: "visible",
        }}
      >
        <Box display="flex" flexDirection="column" gap={1.5}>
          <TextField
            label="ID"
            value={costcenter?.id || ""}
            size="small"
            fullWidth
            disabled
            sx={readOnlyFieldSx}
          />
          <TextField
            label="Costcenter Name"
            name="name"
            size="small"
            fullWidth
            value={form.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            sx={fieldSx}
          />
          <TextField
            label="TIN Number"
            name="tin"
            size="small"
            fullWidth
            value={form.tin}
            onChange={handleChange}
            error={!!errors.tin}
            helperText={errors.tin}
            sx={fieldSx}
          />
          <TextField
            label="IFMIS Code"
            name="ifmisCode"
            size="small"
            fullWidth
            value={form.ifmisCode}
            onChange={handleChange}
            error={!!errors.ifmisCode}
            helperText={errors.ifmisCode}
            sx={fieldSx}
          />
          <Autocomplete
            options={countries}
            value={selectedCountry}
            onChange={handleCountryChange}
            loading={loadingCountries}
            getOptionLabel={(option) => option.Country || ""}
            isOptionEqualToValue={(option, value) =>
              String(option.id) === String(value.id)
            }
            noOptionsText="No countries found"
            clearOnEscape
            disablePortal
            slotProps={{
              popper: {
                placement: "bottom-start",
                modifiers: [{ name: "flip", enabled: false }],
              },
              paper: { sx: { maxHeight: 300 } },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size="small"
                label="Country"
                placeholder="Search country..."
                error={!!errors.countryId}
                helperText={errors.countryId}
                sx={fieldSx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCountries && (
                        <CircularProgress color="inherit" size={18} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Autocomplete
            options={currencies}
            value={selectedCurrency}
            onChange={handleCurrencyChange}
            loading={loadingCurrencies}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) =>
              String(option.id) === String(value.id)
            }
            noOptionsText="No currencies found"
            clearOnEscape
            disablePortal
            slotProps={{
              popper: {
                placement: "bottom-start",
                modifiers: [{ name: "flip", enabled: false }],
              },
              paper: { sx: { maxHeight: 300 } },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size="small"
                label="Currency"
                placeholder="Search currency..."
                error={!!errors.currencyId}
                helperText={errors.currencyId}
                sx={fieldSx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCurrencies && (
                        <CircularProgress color="inherit" size={18} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </DialogContent>

      {/* FOOTER */}
      <Box
        sx={{ height: "1px", bgcolor: alpha(theme.palette.primary.light, 0.4) }}
      />

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.75,
          gap: 1,
          bgcolor: alpha(theme.palette.primary.light, 0.08),
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          size="small"
          sx={{
            textTransform: "none",
            borderColor: alpha(theme.palette.primary.light, 0.5),
            color: "text.secondary",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.light, 0.1),
              borderColor: theme.palette.primary.light,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={saving || saved}
          startIcon={
            saved ? (
              <CheckIcon sx={{ fontSize: 14 }} />
            ) : saving ? (
              <CircularProgress size={13} color="inherit" />
            ) : null
          }
          sx={{
            textTransform: "none",
            minWidth: 115,
            bgcolor: saved
              ? theme.palette.primary.main
              : theme.palette.primary.light,
            "&:hover": { bgcolor: theme.palette.primary.main },
            "&.Mui-disabled": {
              bgcolor: alpha(theme.palette.primary.light, 0.4),
            },
          }}
        >
          {saved ? "Saved!" : saving ? "Saving..." : "Save changes"}
        </Button>
      </DialogActions>
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Dialog>
  );
}
export default EditCostcenterModal;
